import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import moment from 'moment';

import {
  UploadDocumentsDrawerFooterStyled,
  UploadDocumentsDrawerInfoStyled,
  UploadDocumentsDrawerStyled,
  UploadDocumentsDrawerSubtitleStyled,
} from './UploadDocumentsDrawerStyled';
import { Loader } from 'theSource/components';
import { DocumentRequestDateIcon } from 'enrollment/assets/DocumentRequestDateIcon';
import { selectSubscriberDocuments } from 'enrollment/features/ToDoList/services';
import { VALID_UPLOAD_DOCUMENT_TYPES } from 'enrollment/app/constants';
import { MAX_DOCUMENT_SIZE_TO_UPLOAD } from 'enrollment/utils/common/constant';
import { checkForFileSize } from 'enrollment/utils/common/Utils';
import { queryClient, useApiMutation } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';
import { ErrorResponseDto, ResponseDto } from 'theSource/types';
import { UploadedDocumentRequestDto } from 'enrollment/types/dtos';
import {
  GET_SUBSCRIBER_DOCUMENTS_QUERY_KEY,
  useDocumentRequestQuery,
} from 'enrollment/queries';
import { DocumentTypeDto } from 'enrollment/features/ToDoList/types';

interface UploadDocumentsDrawerProps {
  cardName: string;
  id: number;
  termableBenefits?: string[];
  benefitName: string;
  terminateCoverageOn: string;
  requestText: string;
  documentTypeIds: string[];
}

function stripHtmlTags(text: string): string {
  if (!text) {
    return '';
  }

  // First create a temporary element to decode HTML entities
  const doc = new DOMParser().parseFromString(text, 'text/html');
  // Get the text content which handles HTML entities
  const decodedText = doc.body.textContent || '';
  // Remove any remaining HTML tags
  return decodedText.replace(/<[^>]*>?/g, '');
}

export const UploadDocumentsDrawer = ({
  cardName,
  id,
  termableBenefits,
  benefitName,
  terminateCoverageOn,
  requestText,
  documentTypeIds,
}: UploadDocumentsDrawerProps) => {
  const { t } = useTranslation('pages.enrollmentAddYourDocuments');
  const { documentRequestsData } = useDocumentRequestQuery({
    lumosEnabled: true,
  });
  const docRequestData = documentRequestsData?.data;
  const documentRequestApiData = useSelector(selectSubscriberDocuments);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedItem, setSelectedItem] = useState({
    id: id,
    name: cardName,
    electedBenefit: termableBenefits
      ? termableBenefits.join(', ')
      : benefitName,
    pendingBy:
      terminateCoverageOn &&
      moment(terminateCoverageOn).format('MMMM DD, YYYY'),
    documentIds: documentTypeIds,
    requestText: stripHtmlTags(requestText),
  });
  const [chosenDocumentTypeId, setChosenDocumentTypeId] = useState('');
  const [uploadedDocument, setUploadedDocument] = useState('');
  const [uploadedDocumentBase64Value, setUploadedDocumentBase64Value] =
    useState<string | ArrayBuffer | null>('');
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const documentTypes = docRequestData?.document_types;

  const filteredDocumentTypes = (documentIds: string[]) => {
    return documentTypes?.filter((documentType: DocumentTypeDto) => {
      return documentIds.includes(String(documentType?.id));
    });
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
    setSelectedItem({
      id: id,
      name: cardName,
      electedBenefit: termableBenefits
        ? termableBenefits.join(', ')
        : benefitName,
      pendingBy:
        terminateCoverageOn &&
        moment(terminateCoverageOn).format('MMMM DD, YYYY'),
      documentIds: documentTypeIds,
      requestText: stripHtmlTags(requestText),
    });
  };

  const handleRadioChange = (event: any) => {
    setChosenDocumentTypeId(event.target.value);
    handleFileDeletion();
  };

  const handleFileSelection = (file: any) => {
    if (checkForFileSize(file, MAX_DOCUMENT_SIZE_TO_UPLOAD)) {
      return;
    }
    const fileReader = new FileReader();
    fileReader.onloadend = function () {
      setUploadedDocumentBase64Value(fileReader.result);
    };
    fileReader.readAsDataURL(file);
    setUploadedDocument(file);
    setUploadedFileName(file.name);
  };

  const handleFileDeletion = () => {
    setUploadedDocument('');
    setUploadedFileName('');
    setUploadedDocumentBase64Value('');
  };

  const handleFileUpload = (event: any) => {
    const hasFileSelected = event.target.files[0];
    hasFileSelected
      ? handleFileSelection(hasFileSelected)
      : handleFileDeletion();
  };

  useEffect(() => {
    const saveUploadBtn = document.getElementById(
      'upload-document-save-btn'
    ) as HTMLElement;
    if (!uploadedDocument) {
      saveUploadBtn?.setAttribute('disabled', 'true');
      return;
    }
    saveUploadBtn?.removeAttribute('disabled');
  }, [uploadedDocument]);

  useEffect(() => {
    if (chosenDocumentTypeId) {
      const fileUploadComponentId = `file-upload-${chosenDocumentTypeId}`;
      const fileUploadComponent = document.getElementById(
        fileUploadComponentId
      );
      fileUploadComponent &&
        fileUploadComponent.addEventListener('file-change', handleFileUpload);

      return () => {
        fileUploadComponent &&
          fileUploadComponent.removeEventListener(
            'file-change',
            handleFileUpload
          );
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenDocumentTypeId]);

  const handleSuccess = (response: any) => {
    const data =
      response?.data?.updated_document_requests &&
      response?.data?.updated_document_requests[0];
    const submittedDocuments = data?.submitted_documents[0];
    const documentType = documentRequestApiData?.document_types?.find(
      (type: any) => type?.id === submittedDocuments?.document_type_id
    );
    submittedDocuments['document_type_name'] = documentType?.name;
    queryClient.invalidateQueries(GET_SUBSCRIBER_DOCUMENTS_QUERY_KEY);
    queryClient.refetchQueries({
      queryKey: GET_SUBSCRIBER_DOCUMENTS_QUERY_KEY,
    });
    setOpenDrawer(false);
  };

  const { isLoading, mutate: saveUploadedDocument } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    UploadedDocumentRequestDto
  >({
    queryKey: [GET_SUBSCRIBER_DOCUMENTS_QUERY_KEY],
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/documents/subscriber_documents`,
    getErrorMessage: (error) => error.response?.data?.details,
    onSuccess: (response) => handleSuccess(response),
    showSuccessSnackbar: false,
  });

  const handleSave = () => {
    saveUploadedDocument({
      requests: [
        {
          request_id: selectedItem.id,
          selected_document_type_id: chosenDocumentTypeId,
          file: uploadedDocumentBase64Value,
          document_name: uploadedFileName,
        },
      ],
    });
  };

  const handleUploadDrawerClose = () => {
    setOpenDrawer(false);
    setChosenDocumentTypeId('');
  };

  return (
    <>
      <ps-button
        data-testid="doc-req-upload-btn"
        id={`upload-button-${selectedItem?.id}`}
        label={t('common:upload')}
        level="secondary"
        icon-right="upload-simple"
        onClick={handleOpenDrawer}
      />
      <UploadDocumentsDrawerStyled
        open={openDrawer}
        anchor="right"
        id={`upload-documents-drawer-${selectedItem?.id}`}
        onClose={handleUploadDrawerClose}
        role="dialog"
        aria-labelledby="upload_drawer_title"
      >
        <ps-button
          id={`back-button-${selectedItem?.id}`}
          label={t('common:close')}
          level="tertiary"
          icon="x"
          onClick={handleUploadDrawerClose}
          extraClass="p-0 my-3"
        />
        <h1 id="upload_drawer_title">
          {t('UploadDocumentsDrawerTitle') + ` ${selectedItem?.name}`}
        </h1>

        <UploadDocumentsDrawerInfoStyled container>
          <Grid item>
            {selectedItem?.electedBenefit && (
              <Typography variant="body2" gutterBottom>
                {t('ElectedBenefit') + `: ${selectedItem?.electedBenefit}`}
              </Typography>
            )}
          </Grid>
          <Grid item>
            {selectedItem?.pendingBy && (
              <>
                <Typography
                  variant="overline"
                  gutterBottom
                  sx={{ marginRight: '1rem' }}
                  dangerouslySetInnerHTML={{
                    __html: DocumentRequestDateIcon,
                  }}
                />
                <Typography variant="overline" gutterBottom>
                  {t('By') + selectedItem?.pendingBy}
                </Typography>
              </>
            )}
          </Grid>
        </UploadDocumentsDrawerInfoStyled>
        <UploadDocumentsDrawerSubtitleStyled variant="body1" gutterBottom>
          {selectedItem?.requestText}
        </UploadDocumentsDrawerSubtitleStyled>

        {isLoading ? (
          <Loader />
        ) : (
          <FormControl component="fieldset">
            <RadioGroup
              aria-labelledby="radio-document-types-group-label"
              name="radio-document-types-group"
              onChange={handleRadioChange}
            >
              {filteredDocumentTypes(selectedItem.documentIds)?.map(
                (documentRequired: DocumentTypeDto) => {
                  return (
                    <div key={documentRequired.id}>
                      <FormControlLabel
                        value={documentRequired.id}
                        control={<Radio />}
                        label={documentRequired.name}
                      />
                      {chosenDocumentTypeId ===
                        String(documentRequired?.id) && (
                        <ps-file-upload
                          id={`file-upload-${String(documentRequired.id)}`}
                          button-text="Select Files"
                          accepted-file-types={VALID_UPLOAD_DOCUMENT_TYPES}
                          file-size-limit="5"
                        />
                      )}
                    </div>
                  );
                }
              )}
            </RadioGroup>
          </FormControl>
        )}
        <UploadDocumentsDrawerFooterStyled container>
          <Grid marginX="2.4rem" item>
            <ps-button
              data-testid="upload-document-cancel-btn"
              id="upload-document-cancel-btn"
              level="tertiary"
              label={t('common:cancel')}
              onClick={handleUploadDrawerClose}
            />
          </Grid>
          <Grid item>
            <ps-button
              data-testid="upload-document-save-btn"
              id="upload-document-save-btn"
              label={t('common:save')}
              level="primary"
              disabled={true}
              onClick={handleSave}
            />
          </Grid>
        </UploadDocumentsDrawerFooterStyled>
      </UploadDocumentsDrawerStyled>
    </>
  );
};
